// @ts-check
const { getCreditCap } = require("./utils/ubpUtils");
const { getWorkspaceRestricted } = require("./routes/workspace/utils");
const { isCustomEnterprisePlan } = require("@functions/workspacePlans.js");

const {
    featureConversionRate
} = require("@functions/services/billingUsage/billingUsageFeatureConversion");

function hasCreditSystem(usage) {
    return !!usage?.features?.creditSystem;
}

function creditCapRemaining({ creditCap, planCycleUsed }) {
    const currentUsed = planCycleUsed ?? 0;

    if (creditCap === "none") {
        return Infinity;
    } else {
        creditCap = Number(creditCap);
    }

    return Math.max(creditCap - currentUsed, 0);
}

function getCreditCapRemaining({ usage, planCycleUsed }) {
    return creditCapRemaining({ creditCap: getCreditCap({ usage }), planCycleUsed });
}

function calculateCreditCost(usage, feature, unitAmount) {
    const conversionRate = featureConversionRate({ usage, feature });
    return unitAmount * conversionRate;
}

function underCreditCap({ creditCap, planCycleUsed, creditsSpendAmount = 0 }) {
    if (creditsSpendAmount === 0 && typeof creditCap === "number") {
        // Sometimes we don't pass in a creditsSpendAmount, so we just want to check if
        // the cycleCredits so far are within the creditCap, regardless of how much will be spent.
        // e.g. cycleCredits = 30, creditCap = 15, creditsSpendAmount = 0 (because we don't pass it in)
        // This should return false (workspace is over credit cap), but if we don't treat this as an edge case,
        // we'll incorrectly return true because creditCapRemaining() will return 0, and 0 >= 0 is true.
        const currentUsed = planCycleUsed;
        if (typeof currentUsed === "number") {
            return currentUsed <= creditCap;
        }
    }

    const creditsRemaining = creditCapRemaining({ creditCap, planCycleUsed });

    return creditsRemaining >= creditsSpendAmount;
}

function creditSpendingAbility({ usage, planCycleUsed, creditsSpendAmount = 0 }) {
    // If credit system is not enabled, don't check credit spending ability
    if (!hasCreditSystem(usage)) return true;

    if (isCustomEnterprisePlan(usage?.plan)) return true;

    if (getWorkspaceRestricted(usage)) return false;

    return underCreditCap({
        creditCap: getCreditCap({ usage }),
        planCycleUsed,
        creditsSpendAmount
    });
}

module.exports = {
    getCreditCap,
    getCreditCapRemaining,
    calculateCreditCost,
    underCreditCap,
    creditSpendingAbility,
    hasCreditSystem
};
